import { useI18n } from '@affine/i18n';
import type { CompatibilityResult } from '@streamlain/field-types';
import { type FC, useMemo } from 'react';

import { partitionByCompatibility } from '../controls/compatibility-partition';
import * as controlsCss from '../controls/controls.css';
import type { ContentDbColumn } from '../utils/get-content-db-columns';
import * as css from './bindings.css';

export type BindingSource = 'auto' | 'override' | 'unresolved';

export interface BindingRowProps {
  role: string;
  field: string;
  required: boolean;
  current: string | null;
  compatibility: CompatibilityResult;
  source: BindingSource;
  columns: ReadonlyArray<ContentDbColumn>;
  acceptedTypes: ReadonlyArray<string>;
  missing: boolean;
  onChange: (columnId: string | null) => void;
}

const COMPAT_BADGE_KEYS: Record<CompatibilityResult, string> = {
  compatible:
    'com.streamlain.customApps.viewConfig.bindings.compat.badge.compatible',
  castable:
    'com.streamlain.customApps.viewConfig.bindings.compat.badge.castable',
  incompatible:
    'com.streamlain.customApps.viewConfig.bindings.compat.badge.incompatible',
};

const SOURCE_BADGE_KEYS: Record<BindingSource, string> = {
  auto: 'com.streamlain.customApps.viewConfig.bindings.source.auto',
  override: 'com.streamlain.customApps.viewConfig.bindings.source.override',
  unresolved: 'com.streamlain.customApps.viewConfig.bindings.source.unresolved',
};

export const BindingRow: FC<BindingRowProps> = ({
  role,
  field,
  required,
  current,
  compatibility,
  source,
  columns,
  acceptedTypes,
  missing,
  onChange,
}) => {
  const t = useI18n();
  const partitioned = useMemo(
    () => partitionByCompatibility(columns, acceptedTypes),
    [columns, acceptedTypes]
  );
  const bindingKey = `${role}:${field}`;

  return (
    <div
      className={missing ? `${css.row} ${css.rowMissing}` : css.row}
      data-sl-role="binding-row"
      data-sl-field={bindingKey}
      data-sl-missing={missing ? 'true' : 'false'}
      data-sl-source={source}
    >
      <div className={css.rowHeader}>
        <span className={css.roleLabel}>
          {role}.{field}
          {required ? (
            <span className={css.requiredBadge}>
              {t['com.streamlain.customApps.viewConfig.bindings.required']()}
            </span>
          ) : null}
        </span>
        <span className={css.badges}>
          <span
            className={css.compatBadge[compatibility]}
            data-sl-compat={compatibility}
          >
            {t[COMPAT_BADGE_KEYS[compatibility]]()}
          </span>
          <span className={css.sourceBadge}>
            {t[SOURCE_BADGE_KEYS[source]]()}
          </span>
        </span>
      </div>
      <select
        className={controlsCss.select}
        value={current ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
        data-sl-control="binding-select"
      >
        {!required ? (
          <option value="">
            {t['com.streamlain.customApps.viewConfig.column.none']()}
          </option>
        ) : null}
        {required && current === null ? (
          <option value="" disabled>
            {t['com.streamlain.customApps.viewConfig.column.choose']()}
          </option>
        ) : null}
        {partitioned.compatible.length > 0 ? (
          <optgroup
            label={t[
              'com.streamlain.customApps.viewConfig.bindings.compat.compatible'
            ]()}
          >
            {partitioned.compatible.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.type})
              </option>
            ))}
          </optgroup>
        ) : null}
        {partitioned.castable.length > 0 ? (
          <optgroup
            label={t[
              'com.streamlain.customApps.viewConfig.bindings.compat.castable'
            ]()}
          >
            {partitioned.castable.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.type})
              </option>
            ))}
          </optgroup>
        ) : null}
        {partitioned.incompatible.length > 0 ? (
          <optgroup
            label={t[
              'com.streamlain.customApps.viewConfig.bindings.compat.incompatible'
            ]()}
          >
            {partitioned.incompatible.map(c => (
              <option key={c.id} value={c.id} disabled>
                {c.name} ({c.type})
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
    </div>
  );
};
